/**
 * Engage Reports — one table, filters added on demand.
 *
 * The date range is the only filter on screen by default. Everything else is
 * added deliberately: click "Add filter", pick a dimension, pick a value, and it
 * becomes a removable chip. Chips accumulate and each is removed independently,
 * so a manager can build "OP patients, from walk-ins, in Cardiology" one step at
 * a time and peel any single condition back off without resetting the rest.
 *
 * Output is a single breakdown table. "Group by" changes which dimension forms
 * the rows — that one control is what lets one table answer what used to be
 * eleven separate report screens. Grouping happens over rows the server already
 * fetched, so switching dimension costs no extra query.
 *
 * Data comes from engage/analytics/command-center/?slim=1 — one request. The
 * slim flag skips the panels this screen no longer renders.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart3, RefreshCw, Plus, X, Calendar, ChevronDown,
} from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import { cn } from '../../../utils/tailwindUtils';
import { ENGAGE_SOURCE_LABEL, ENGAGE_STATUS_LABEL } from '../../../utils/engageStageBadge';
import { presetToRange } from './ReportFilters';

const DATE_PRESETS = [
  { value: 'last_30',    label: 'Last 30 days' },
  { value: 'last_7',     label: 'Last 7 days' },
  { value: 'today',      label: 'Today' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_90',    label: 'Last 90 days' },
  { value: 'all',        label: 'All time' },
  { value: 'custom',     label: 'Custom range' },
];

/**
 * The dimensions a chip can be built from. `options` derives each value list
 * from the server's filter_options, so nothing here is hardcoded.
 * `requires` gates a dimension until its parent chip exists — a stage belongs to
 * exactly one patient-type ladder, so offering it first would be meaningless.
 */
const DIMENSIONS = [
  {
    key: 'patient_type', label: 'Patient type',
    options: (o) => (o?.patient_types || []).map(t => ({ value: String(t.id), label: t.name })),
  },
  {
    key: 'stage', label: 'Stage', requires: 'patient_type',
    options: (o, active) => {
      const pt = active.find(c => c.key === 'patient_type')?.value;
      const t = (o?.patient_types || []).find(x => String(x.id) === String(pt));
      return (t?.stages || []).map(s => ({ value: String(s.id), label: s.name }));
    },
  },
  {
    key: 'source', label: 'Source',
    options: (o) => (o?.sources || []).map(s => ({
      value: s, label: ENGAGE_SOURCE_LABEL[s] || s.replace(/_/g, ' '),
    })),
  },
  {
    key: 'department', label: 'Department',
    options: (o) => (o?.departments || []).map(d => ({ value: d, label: d })),
  },
  {
    key: 'assigned_to', label: 'Assigned to',
    options: (o) => (o?.staff || []).map(s => ({ value: String(s.id), label: s.name })),
  },
  {
    key: 'referring_doctor', label: 'Referring doctor',
    options: (o) => (o?.referring_doctors || []).map(d => ({ value: String(d.id), label: d.name })),
  },
  {
    key: 'corporate', label: 'Corporate account',
    options: (o) => (o?.corporates || []).map(c => ({ value: String(c.id), label: c.company_name })),
  },
];

const inr = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

export default function ReportsDashboard() {
  const { fetch: apiFetch } = useApiMethods();

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [tick, setTick]       = useState(0);

  const [preset, setPreset]         = useState('last_90');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo]     = useState('');
  const [groupBy, setGroupBy]       = useState('patient_type');

  // Active filter chips: [{ key, label, value, valueLabel }]
  const [chips, setChips] = useState([]);

  const opts = data?.filter_options;

  const chipParams = useMemo(() => {
    const p = {};
    for (const c of chips) p[c.key] = c.value;
    return p;
  }, [chips]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { from, to } = presetToRange(preset, customFrom, customTo);
        const q = new URLSearchParams();
        if (from) q.set('from', from);
        if (to)   q.set('to', to);
        q.set('group_by', groupBy);
        q.set('slim', '1');
        Object.entries(chipParams).forEach(([k, v]) => q.set(k, v));
        const resp = await apiFetch(`engage/analytics/command-center/?${q.toString()}`);
        if (cancelled) return;
        setData(resp?.data || resp || null);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, customFrom, customTo, groupBy, chipParams, tick]);

  // A new chip lands on "All" — it is a column you have opened, not yet a
  // narrowing. You then change it to whichever value you want, in place.
  // 'all' is the server's ignore sentinel, so an untouched chip filters nothing.
  const addChip = (dim) => {
    setChips(cs => [...cs.filter(c => c.key !== dim.key),
                    { key: dim.key, label: dim.label, value: 'all' }]);
  };

  const setChipValue = (key, value) => {
    setChips(cs => {
      const next = cs.map(c => (c.key === key ? { ...c, value } : c));
      // Resetting a parent back to All makes its dependants meaningless — a
      // stage only exists inside one patient-type ladder.
      if (value === 'all') {
        return next.filter(c => {
          const d = DIMENSIONS.find(x => x.key === c.key);
          return !(d?.requires === key);
        });
      }
      // Switching the parent to a different value invalidates the child's
      // selection, since that stage belongs to the previous ladder.
      return next.map(c => {
        const d = DIMENSIONS.find(x => x.key === c.key);
        return d?.requires === key ? { ...c, value: 'all' } : c;
      });
    });
  };

  const removeChip = (key) => {
    setChips(cs => {
      const next = cs.filter(c => c.key !== key);
      // Removing a parent invalidates anything that depended on it.
      const orphaned = DIMENSIONS.filter(d => d.requires && !next.some(c => c.key === d.requires));
      return next.filter(c => !orphaned.some(d => d.key === c.key));
    });
  };

  const b = data?.breakdown;
  const k = data?.kpis;

  return (
    <div className="p-4 max-w-[1500px] mx-auto space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
        <h1 className="text-lg font-bold text-gray-900 dark:text-white whitespace-nowrap">
          Hospital Reports
        </h1>
        {data && (
          <span className="text-[13px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
            {data.filters.from} → {data.filters.to}
          </span>
        )}
        <button onClick={() => setTick(t => t + 1)}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-[13px] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
          <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} /> Refresh
        </button>
      </div>

      {/* ── Filter bar: date is permanent, the rest are chips you opt into ── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-3.5 border border-gray-100 dark:border-gray-700 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/60">
            <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            <select value={preset} onChange={e => setPreset(e.target.value)}
              className="bg-transparent text-[13px] font-medium text-blue-800 dark:text-blue-200 focus:outline-none cursor-pointer">
              {DATE_PRESETS.map(p => (
                <option key={p.value} value={p.value} className="text-gray-900">{p.label}</option>
              ))}
            </select>
          </span>

          {preset === 'custom' && (
            <>
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className={ctl} />
              <span className="text-gray-400">→</span>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className={ctl} />
            </>
          )}

          {chips.map(c => {
            const dim = DIMENSIONS.find(d => d.key === c.key);
            const values = dim ? dim.options(opts, chips) : [];
            const active = c.value !== 'all';
            return (
              <span key={c.key}
                className={cn('inline-flex items-center gap-1.5 pl-3 pr-1 py-1 rounded-lg border',
                  active
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/60'
                    : 'bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-600 border-dashed')}>
                <span className={cn('text-[13px] whitespace-nowrap',
                  active ? 'text-blue-700 dark:text-blue-300' : 'text-gray-500 dark:text-gray-400')}>
                  {c.label}:
                </span>
                {/* The value is editable in place — pick All to widen it back
                    without losing the chip. */}
                <select value={c.value} onChange={e => setChipValue(c.key, e.target.value)}
                  className={cn('bg-transparent text-[13px] font-semibold focus:outline-none cursor-pointer max-w-[190px] py-1',
                    active ? 'text-blue-900 dark:text-blue-100' : 'text-gray-600 dark:text-gray-300')}>
                  <option value="all" className="text-gray-900">All</option>
                  {values.map(o => (
                    <option key={o.value} value={o.value} className="text-gray-900">{o.label}</option>
                  ))}
                </select>
                <button onClick={() => removeChip(c.key)} title={`Remove the ${c.label} filter`}
                  className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400 hover:text-red-500">
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            );
          })}

          <AddFilter opts={opts} chips={chips} onAdd={addChip} />

          {chips.length > 1 && (
            <button onClick={() => setChips([])}
              className="text-[13px] text-gray-500 hover:text-red-500 underline ml-1">
              Clear all
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-[13px] text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* ── The single table ── */}
      <div className={cn('bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden transition-opacity',
        loading && data && 'opacity-60')}>
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-gray-700">
          <span className="text-[13px] text-gray-500 dark:text-gray-400">Group by</span>
          <select value={groupBy} onChange={e => setGroupBy(e.target.value)} className={ctl}>
            {(data?.group_by_options || []).map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {k && (
            <span className="ml-auto text-[13px] text-gray-600 dark:text-gray-300">
              <b className="text-gray-900 dark:text-white">{k.enquiries}</b> enquiries ·{' '}
              <b className="text-gray-900 dark:text-white">{k.converted}</b> converted ·{' '}
              <b className="text-gray-900 dark:text-white">{k.conversion_rate_pct}%</b> ·{' '}
              <b className="text-gray-900 dark:text-white">₹{inr(k.realised_revenue)}</b> realised
            </span>
          )}
        </div>

        {!data && loading ? (
          <p className="py-16 text-center text-[13px] text-gray-500">Loading…</p>
        ) : !b?.rows?.length ? (
          <p className="py-16 text-center text-[13px] text-gray-400">
            No enquiries match these filters.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-gray-50 dark:bg-gray-900/40 text-[12px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold whitespace-nowrap">{b.group_label}</th>
                  <th className="px-4 py-2.5 text-right font-semibold whitespace-nowrap">Enquiries</th>
                  <th className="px-4 py-2.5 text-right font-semibold whitespace-nowrap">Active</th>
                  <th className="px-4 py-2.5 text-right font-semibold whitespace-nowrap">Converted</th>
                  <th className="px-4 py-2.5 text-right font-semibold whitespace-nowrap">Lost</th>
                  <th className="px-4 py-2.5 text-right font-semibold whitespace-nowrap">Conv. rate</th>
                  <th className="px-4 py-2.5 text-right font-semibold whitespace-nowrap">Realised ₹</th>
                  <th className="px-4 py-2.5 text-right font-semibold whitespace-nowrap">Pipeline ₹</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {b.rows.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-2.5 text-gray-900 dark:text-white font-medium max-w-[260px] truncate">
                      {groupBy === 'source'
                        ? (ENGAGE_SOURCE_LABEL[r.label] || r.label)
                        : groupBy === 'status'
                          ? (ENGAGE_STATUS_LABEL[r.label] || r.label)
                          : r.label}
                    </td>
                    <Num>{r.enquiries}</Num>
                    <Num>{r.active}</Num>
                    <Num>{r.converted}</Num>
                    <Num>{r.lost}</Num>
                    <Num>{r.conversion_rate_pct}%</Num>
                    <Num>₹{inr(r.realised_revenue)}</Num>
                    <Num>₹{inr(r.pipeline_revenue)}</Num>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 dark:bg-gray-900/40 border-t-2 border-gray-200 dark:border-gray-600 font-semibold">
                <tr>
                  <td className="px-4 py-2.5 text-gray-900 dark:text-white">Total</td>
                  <Num>{b.totals.enquiries}</Num>
                  <Num>{b.totals.active}</Num>
                  <Num>{b.totals.converted}</Num>
                  <Num>{b.totals.lost}</Num>
                  <Num>{b.totals.conversion_rate_pct}%</Num>
                  <Num>₹{inr(b.totals.realised_revenue)}</Num>
                  <Num>₹{inr(b.totals.pipeline_revenue)}</Num>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {k?.value_quality?.estimated > 0 && (
        <p className="text-[12px] text-gray-500 dark:text-gray-400 px-1">
          Revenue blends {k.value_quality.quoted} quoted · {k.value_quality.catalog} from the
          procedure catalog · {k.value_quality.estimated} estimated from department ARPP.
        </p>
      )}
    </div>
  );
}

const ctl = 'px-2.5 py-2 text-[13px] rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500';

function Num({ children }) {
  return (
    <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-300 tabular-nums whitespace-nowrap">
      {children}
    </td>
  );
}

/**
 * Pick a dimension to open as a chip. One step only — the chip lands on "All"
 * and its value is chosen there, so this menu never has to nest a value list.
 *
 * Dimensions already open are hidden. A dimension whose parent is not narrowed
 * to a specific value is shown but disabled, with the reason spelled out rather
 * than silently absent.
 */
function AddFilter({ opts, chips, onAdd }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
  }, [open]);

  const available = DIMENSIONS.filter(d => !chips.some(c => c.key === d.key));

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)} disabled={!available.length}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-[13px] text-gray-600 dark:text-gray-300 hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 disabled:opacity-40">
        <Plus className="w-4 h-4" /> Add filter
        <ChevronDown className="w-3.5 h-3.5" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 left-0 w-60 max-h-80 overflow-y-auto bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg py-1">
          <p className="px-3 py-1.5 text-[12px] uppercase tracking-wide text-gray-400 font-semibold">
            Filter by
          </p>
          {available.map(d => {
            // The parent must be narrowed to a real value, not merely present —
            // "Stage" under "Patient type: All" has no single ladder to draw from.
            const parentChip = d.requires ? chips.find(c => c.key === d.requires) : null;
            const blocked = !!d.requires && (!parentChip || parentChip.value === 'all');
            const parent = DIMENSIONS.find(x => x.key === d.requires)?.label;
            return (
              <button key={d.key} disabled={blocked}
                onClick={() => { onAdd(d); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-[13px] text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:hover:bg-transparent">
                {d.label}
                {blocked && (
                  <span className="block text-[12px] text-gray-400">pick a {parent} first</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
